import { queryOptions, useQuery } from '@tanstack/react-query';

import { WorkloadOptimizationEventsSummary } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WOOP_EVENTS_SUMMARY_QUERY_KEY = 'woop-events-summary';

export type UseWorkloadEventsSummaryQueryArgs = {
  clusterId: string;
  workloadId?: string;
  workloadName?: string;
  namespace?: string;
  kind?: string;
  fromDate?: string;
  toDate?: string;
  queryKey: string;
  enabled?: boolean;
};

type UseWorkloadEventsSummaryQuery = {
  isLoading: boolean;
  data?: WorkloadOptimizationEventsSummary;
  error?: unknown;
  refetch: () => void;
};

const getWorkloadAutoscalerEventsSummaryQueryKey = (
  params: {
    clusterId?: string;
    workloadId?: string;
    workloadName?: string;
    namespace?: string;
    kind?: string;
    queryKey?: string;
  } = {}
) =>
  [
    WOOP_EVENTS_SUMMARY_QUERY_KEY,
    params.queryKey,
    params.clusterId,
    params.workloadId,
    params.workloadName,
    params.namespace,
    params.kind,
  ] as const;

const getWorkloadAutoscalerEventsSummaryQueryOptions = ({
  clusterId,
  workloadId,
  workloadName,
  namespace,
  fromDate,
  toDate,
  queryKey,
  kind,
  enabled = true,
}: UseWorkloadEventsSummaryQueryArgs) =>
  queryOptions({
    queryKey: getWorkloadAutoscalerEventsSummaryQueryKey({
      clusterId,
      workloadId,
      workloadName,
      namespace,
      kind,
      queryKey,
    }),
    queryFn: async () => {
      const { data } = await apiClient.workloadOptimization.getEventsSummary({
        clusterId,
        workloadId,
        fromDate,
        toDate,
        workloadName,
        workloadNamespace: namespace,
        workloadKind: kind,
      });
      return data;
    },
    enabled,
  });

export const useWorkloadAutoscalerEventsSummaryQuery = (
  args: UseWorkloadEventsSummaryQueryArgs
): UseWorkloadEventsSummaryQuery => {
  const { isLoading, data, error, refetch } = useQuery(
    getWorkloadAutoscalerEventsSummaryQueryOptions(args)
  );

  return {
    isLoading,
    data,
    error,
    refetch,
  };
};
